import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AuthDialog from "@/components/AuthDialog";
import { HeroSection } from "@/components/HeroSection";
// import { VideoSection } from "@/components/VideoSection";
import { AnimatedProcessFlow } from "@/components/AnimatedProcessFlow";
import { ProductShowcaseSection } from "@/components/ProductShowcaseSection";
import { UseCaseSection } from "@/components/UseCaseSection";
import { AffiliateSection } from "@/components/AffiliateSection";
import { FAQSection } from "@/components/FAQSection";
import { CTASection } from "@/components/CTASection";
import { useRemoteConfigBoolean, RemoteConfigKeys } from "@/hooks/useRemoteConfig";
import { generateOrganizationSchema, generateWebSiteSchema } from "@/lib/structured-data";
import { getDefaultMetaTags } from "@/lib/seo-utils";

import { LazyCarouselSection } from "@/components/LazyCarouselSection";

export default function Index() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const { value: allowAffiliates } = useRemoteConfigBoolean(
    RemoteConfigKeys.ALLOW_AFFILIATES,
    false
  );

  // Function to handle navigation with scroll to top
  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  // Handle the "Create Design" button click
  const handleCreateDesign = () => {
    if (user) {
      // If user is authenticated, navigate directly to new project page
      handleNavigation("/projects/new");
    } else {
      // If user is not authenticated, open the auth dialog
      setIsAuthDialogOpen(true);
    }
  };

  const metaTags = getDefaultMetaTags();
  const organizationSchema = generateOrganizationSchema();
  const webSiteSchema = generateWebSiteSchema();

  return (
    <>
      <Helmet>
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />
        <meta name="keywords" content={metaTags.keywords} />

        {/* Open Graph tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={metaTags.ogUrl} />
        <meta property="og:title" content={metaTags.ogTitle} />
        <meta property="og:description" content={metaTags.ogDescription} />
        <meta property="og:image" content={metaTags.ogImage} />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={metaTags.ogUrl} />
        <meta name="twitter:title" content={metaTags.twitterTitle} />
        <meta name="twitter:description" content={metaTags.twitterDescription} />
        <meta name="twitter:image" content={metaTags.twitterImage} />

        {/* Canonical URL */}
        <link rel="canonical" href={metaTags.ogUrl} />

        {/* Structured Data - Organization Schema */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>

        {/* Structured Data - WebSite Schema */}
        <script type="application/ld+json">{JSON.stringify(webSiteSchema)}</script>
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          {/* Hero Section */}
          <HeroSection onCreateDesign={handleCreateDesign} />

          {/* Video Section */}
          {/* <VideoSection /> */}

          {/* Sticker Carousel Section - Lazy-loaded for PageSpeed */}
          <LazyCarouselSection />

          {/* Product Showcase */}
          <ProductShowcaseSection />

          {/* How It Works - Process Flow */}
          <AnimatedProcessFlow />

          {/* Use Cases */}
          <UseCaseSection />

          {/* Affiliate Program - controlled by Remote Config */}
          {allowAffiliates && <AffiliateSection />}

          {/* FAQ Section */}
          <FAQSection />

          {/* CTA Section */}
          <CTASection onCreateDesign={handleCreateDesign} />
        </main>
        <Footer />
        {/* Auth Dialog for unauthenticated users */}
        <AuthDialog
          isOpen={isAuthDialogOpen}
          onOpenChange={setIsAuthDialogOpen}
          showFreeTokensMessage={true}
        />
      </div>
    </>
  );
}
